import type React from 'react';
import { FilePdfIcon, FilePowerpointIcon, FileWordIcon, MailAttachmentIcon } from '@/components/ui/icons';
import { FileImage, FileSpreadsheet, FileText, Mail } from 'lucide-react';
import { isImageFileName, isPowerPointFileName, isSpreadsheetFileName, isTextFileName } from '@/utils/file-utils';

type AlignableIcon = React.ComponentType<{
  className?: string;
  viewBox?: string;
  preserveAspectRatio?: string;
}>;

/** Crop built-in icon padding and pin the remaining artwork to the left of the slot. */
function leftAlignedIcon(Icon: AlignableIcon, viewBox: string): AlignableIcon {
  return function AlignedIcon({ className }) {
    return <Icon className={className} viewBox={viewBox} preserveAspectRatio="xMinYMid meet" />;
  };
}

const LeftAlignedFileText = leftAlignedIcon(FileText, '3 1 18 22');
const LeftAlignedFileSpreadsheet = leftAlignedIcon(FileSpreadsheet, '3 1 18 22');
const LeftAlignedFileImage = leftAlignedIcon(FileImage, '3 1 18 22');
const LeftAlignedMail = leftAlignedIcon(Mail, '1 3 22 18');
const LeftAlignedFileWord = leftAlignedIcon(FileWordIcon, '0 0 384 512');
// Keep a gutter around the circle and the tile. A viewBox tangent to either one
// clips the mark, and this wrapper is shared by every getDocumentIcon caller.
const LeftAlignedFilePowerpoint = leftAlignedIcon(FilePowerpointIcon, '0.5 1.5 31.12 29');
const LeftAlignedMailAttachment = leftAlignedIcon(MailAttachmentIcon, '1 3 23 21');

export interface DocumentIconInfo {
  Icon: React.ComponentType<{ className?: string }>;
  colorClass: string;
  fileType: 'Email' | 'Email with attachments' | 'PDF' | 'Word' | 'PowerPoint' | 'Excel' | 'Image' | 'Text' | 'Other';
}

/**
 * Get the appropriate icon and color for a document based on its filename.
 * This is used consistently across the correspondence table and tasks table.
 *
 * @param fileName - The document filename (e.g., "document.pdf", "email.eml")
 * @param hasAttachments - Whether the document has attachments (for emails)
 * @returns Object containing the Icon component, color class, and file type
 */
export function getDocumentIcon(fileName: string | undefined | null, hasAttachments: boolean = false): DocumentIconInfo {
  if (!fileName) {
    return {
      Icon: LeftAlignedFileText,
      colorClass: 'text-gray-400',
      fileType: 'Other'
    };
  }
  const lowerFileName = fileName.toLowerCase();

  const isEmail = lowerFileName.endsWith('.msg') || lowerFileName.endsWith('.eml');
  const isPdf = lowerFileName.endsWith('.pdf');
  const isWord = lowerFileName.endsWith('.docx') || lowerFileName.endsWith('.doc');
  const isPowerPoint = isPowerPointFileName(fileName);
  const isExcel = isSpreadsheetFileName(fileName);
  const isImage = isImageFileName(fileName);

  if (isPdf) {
    return {
      Icon: FilePdfIcon,
      colorClass: 'text-red-600',
      fileType: 'PDF'
    };
  }

  if (isWord) {
    return {
      Icon: LeftAlignedFileWord,
      colorClass: 'text-blue-700',
      fileType: 'Word'
    };
  }

  if (isPowerPoint) {
    return {
      Icon: LeftAlignedFilePowerpoint,
      colorClass: 'text-orange-700',
      fileType: 'PowerPoint'
    };
  }

  if (isExcel) {
    return {
      Icon: LeftAlignedFileSpreadsheet,
      colorClass: 'text-emerald-700',
      fileType: 'Excel'
    };
  }

  if (isImage) {
    return {
      Icon: LeftAlignedFileImage,
      colorClass: 'text-sky-700',
      fileType: 'Image'
    };
  }

  if (isTextFileName(fileName)) {
    return {
      Icon: FileText,
      colorClass: 'text-slate-600',
      fileType: 'Text'
    };
  }

  if (isEmail && hasAttachments) {
    return {
      Icon: LeftAlignedMailAttachment,
      colorClass: 'text-gray-500',
      fileType: 'Email with attachments',
    };
  }

  if (isEmail) {
    return {
      Icon: LeftAlignedMail,
      colorClass: 'text-gray-500',
      fileType: 'Email',
    };
  }

  // Default to generic file icon for unknown types
  return {
    Icon: LeftAlignedFileText,
    colorClass: 'text-gray-500',
    fileType: 'Other'
  };
}

/**
 * Render a file icon as a React element based on filename.
 * Convenience wrapper around getDocumentIcon for use in bundle components.
 *
 * @param fileName - The document filename
 * @param className - Optional CSS class for the icon size (default: "h-4 w-4")
 * @param greyedOut - If true, uses gray color instead of the default color
 * @returns JSX element with the appropriate icon
 */
export function renderFileIcon(
  fileName?: string,
  className: string = "h-4 w-4",
  greyedOut: boolean = false
): React.ReactElement {
  if (!fileName) {
    return <LeftAlignedFileText className={`${className} text-gray-400`} />;
  }

  const { Icon, colorClass } = getDocumentIcon(fileName);
  const finalColorClass = greyedOut ? 'text-gray-400' : colorClass;

  return <Icon className={`${className} ${finalColorClass}`} />;
}
